import { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, Plus, Trash2, AlertCircle, Layers } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { customRoleApi, ApiError } from '@solara/api-client';
import type { CustomRoleDto, RoleCatalogDto, RolePermissions } from '@solara/api-client';

interface DraftRole {
  id?: string;
  name: string;
  description: string;
  permissions: RolePermissions;
}

const EMPTY_DRAFT = (): DraftRole => ({
  name: '',
  description: '',
  permissions: {},
});

function permCount(perms: RolePermissions): number {
  let n = 0;
  for (const mod of Object.values(perms)) {
    for (const v of Object.values(mod)) if (v) n += 1;
  }
  return n;
}

export default function CustomRolesPage() {
  const { t } = useTranslation('p1');
  const [roles, setRoles] = useState<CustomRoleDto[]>([]);
  const [catalog, setCatalog] = useState<RoleCatalogDto | null>(null);
  const [draft, setDraft] = useState<DraftRole | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      const [list, cat] = await Promise.all([customRoleApi.list(), customRoleApi.catalog()]);
      setRoles(list);
      setCatalog(cat);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  function startEdit(role: CustomRoleDto) {
    setDraft({
      id: role.id,
      name: role.name,
      description: role.description ?? '',
      permissions: JSON.parse(JSON.stringify(role.permissions ?? {})),
    });
  }

  function togglePerm(module: string, action: string) {
    if (!draft) return;
    const modulePerms = draft.permissions[module] ?? {};
    const current = modulePerms[action] === true;
    const nextModule = { ...modulePerms, [action]: !current };
    const nextPerms = { ...draft.permissions, [module]: nextModule };
    if (!nextModule[action]) delete nextModule[action];
    if (Object.keys(nextModule).length === 0) delete nextPerms[module];
    setDraft({ ...draft, permissions: nextPerms });
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      const body = {
        name: draft.name,
        description: draft.description || undefined,
        permissions: draft.permissions,
      };
      if (draft.id) {
        await customRoleApi.update(draft.id, body);
      } else {
        await customRoleApi.create(body);
      }
      setDraft(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(role: CustomRoleDto) {
    if (!window.confirm(t('roles.confirmDelete', { name: role.name }))) return;
    setBusy(true);
    try {
      await customRoleApi.remove(role.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.deleteFailed'));
    } finally {
      setBusy(false);
    }
  }

  const modules = catalog?.modules ?? [];
  const actions = catalog?.actions ?? [];

  const summary = useMemo(
    () => ({
      total: roles.length,
      avgPerms: roles.length
        ? Math.round(roles.reduce((s, r) => s + permCount(r.permissions), 0) / roles.length)
        : 0,
    }),
    [roles],
  );

  return (
    <div className="space-y-6 max-w-6xl">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('roles.title')}</h1>
          <p className="text-fg-muted text-sm">{t('roles.subtitle')}</p>
        </div>
        <Button onClick={() => setDraft(EMPTY_DRAFT())}>
          <Plus className="mr-2 h-4 w-4" />
          {t('roles.newRole')}
        </Button>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="rounded-2xl">
          <CardBody>
            <p className="text-xs text-fg-muted uppercase tracking-wide">{t('roles.rolesTotal')}</p>
            <p className="text-3xl font-semibold mt-2">{summary.total}</p>
          </CardBody>
        </Card>
        <Card className="rounded-2xl">
          <CardBody>
            <p className="text-xs text-fg-muted uppercase tracking-wide">{t('roles.avgPerms')}</p>
            <p className="text-3xl font-semibold mt-2">{summary.avgPerms}</p>
          </CardBody>
        </Card>
        <Card className="rounded-2xl">
          <CardBody>
            <p className="text-xs text-fg-muted uppercase tracking-wide">{t('roles.catalog')}</p>
            <p className="text-3xl font-semibold mt-2">
              {t('roles.catalogValue', { modules: modules.length, actions: actions.length })}
            </p>
          </CardBody>
        </Card>
      </div>

      {draft && catalog && (
        <Card className="rounded-2xl border-brand-500/30">
          <CardHeader>
            <CardTitle>{draft.id ? t('roles.editRole') : t('roles.newRole')}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label={t('roles.roleName')}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder={t('roles.rolePlaceholder')}
              />
              <Input
                label={t('common.description')}
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-start p-2 border-b border-border">{t('roles.module')}</th>
                    {actions.map((a) => (
                      <th key={a} className="p-2 border-b border-border text-center capitalize">
                        {a.replace(/_/g, ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modules.map((m) => (
                    <tr key={m} className="border-b border-border/60">
                      <td className="p-2 font-medium capitalize">{m.replace(/_/g, ' ')}</td>
                      {actions.map((a) => {
                        const checked = draft.permissions[m]?.[a] === true;
                        return (
                          <td key={a} className="p-2 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => togglePerm(m, a)}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-fg-muted">
              {t('roles.permsSelected', { count: permCount(draft.permissions) })}
            </p>

            <div className="flex gap-2">
              <Button
                onClick={save}
                disabled={busy || draft.name.length < 2 || permCount(draft.permissions) === 0}
              >
                {busy
                  ? t('actions.saving')
                  : draft.id
                    ? t('actions.saveChanges')
                    : t('actions.create')}
              </Button>
              <Button variant="ghost" onClick={() => setDraft(null)}>
                {t('actions.cancel')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            {t('roles.rolesCount', { count: roles.length })}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {roles.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('roles.noRoles')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {roles.map((r) => {
                const modKeys = Object.keys(r.permissions ?? {});
                return (
                  <li key={r.id} className="py-3 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{r.name}</p>
                          <Badge tone="info" className="text-[10px]">
                            {t('roles.permsBadge', { count: permCount(r.permissions) })}
                          </Badge>
                          <Badge tone="neutral" className="text-[10px] inline-flex items-center gap-1">
                            <Layers className="h-3 w-3" />
                            {t('roles.modulesBadge', { count: modKeys.length })}
                          </Badge>
                        </div>
                        {r.description && (
                          <p className="text-xs text-fg-muted mt-1">{r.description}</p>
                        )}
                        <p className="text-xs text-fg-muted mt-1">
                          {t('common.updatedAt')} {new Date(r.updatedAt).toLocaleDateString()}
                          {modKeys.length > 0 && (
                            <>
                              {' · '}
                              <span title={modKeys.join(', ')}>{modKeys.slice(0, 4).join(', ')}</span>
                              {modKeys.length > 4 && '…'}
                            </>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" onClick={() => startEdit(r)}>
                          {t('actions.edit')}
                        </Button>
                        <Button variant="ghost" onClick={() => void remove(r)} disabled={busy}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
